import { useEffect, useRef, useState, type DragEvent } from 'react';
import {
  Badge,
  Button,
  Input,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
  Skeleton,
  SkeletonItem,
  Spinner,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  ChevronDown16Regular,
  ChevronRight16Regular,
  Briefcase16Regular,
  People16Regular,
  FolderAdd20Regular,
  MailArrowUp20Regular,
  TabAddRegular,
  MoreHorizontalRegular,
  PeopleLock20Regular,
  LockClosed12Regular,
  Star20Regular,
  Star20Filled,
  Star16Filled,
} from '@fluentui/react-icons';
import type { Container } from '@/types/domain';
import { useFolders } from '@/hooks/useFolders';
import { useRootMails } from '@/hooks/useMails';
import { useCreateFolder } from '@/hooks/useCreateFolder';
import { useFileMail } from '@/features/file-mail/useFileMail';
import { useFileMailToasts } from '@/features/multi-select/useFileMailToasts';
import { useDropFiler } from '@/features/file-mail/useDropFiler';
import { useMailDropStore } from '@/stores/mailDropStore';
import { extractDroppedFiles } from '@/services/office/dragDrop';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';
import { useSelectionStore } from '@/stores/selectionStore';
import { buildFolderTree } from './buildTree';
import { FolderRow } from './FolderRow';
import { ContainerRootMails } from './ContainerRootMails';
import { useTreeStore } from '@/stores/treeStore';
import { useRecentProjectsStore } from '@/stores/recentProjectsStore';
import { useFavoriteIds, useToggleFavorite } from '@/hooks/useFavorites';
import { ContainerVisibilityDialog } from '@/features/visibility/ContainerVisibilityDialog';
import { CollaborationMenu } from './CollaborationMenu';
import { menuPositioning, pointTarget, type CursorTarget } from '@/components/cursorMenu';

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXS}`,
    cursor: 'pointer',
    borderRadius: tokens.borderRadiusSmall,
    fontWeight: tokens.fontWeightSemibold,
    ':hover': { backgroundColor: tokens.colorSubtleBackgroundHover },
  },
  // Mail-drop highlight when a dragged mail targets this project's ROOT
  // (dropped on the header / a root mail / blank space inside the project, i.e.
  // anywhere that isn't a specific folder). Mirrors FolderRow.rowDropOver.
  headerRootDropOver: {
    outline: `2px dashed ${tokens.colorBrandStroke1}`,
    outlineOffset: '-2px',
    backgroundColor: tokens.colorBrandBackground2,
  },
  // Titre + cadenas de visibilité restreinte, COLLÉS l'un à l'autre : le cadenas
  // qualifie le nom de l'espace, il ne doit donc pas flotter à la distance de
  // `gap` du conteneur, ni être repoussé au bout de la ligne. C'est ce wrapper
  // qui porte le `flex: 1` (le titre garde l'ellipsis), sinon le cadenas serait
  // rejeté hors de la zone de troncature.
  labelWrap: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
  label: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  // A little breathing room between the project/space icon and its label.
  headerIcon: { marginRight: tokens.spacingHorizontalXS, flexShrink: 0 },
  inactive: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  archived: { textDecoration: 'line-through' },
  body: {
    // Indent the whole project subtree one step in from the header so a
    // first-level folder can't be mistaken for another project row (both show
    // a chevron + icon + name — previously only the icon set them apart). The
    // extra offset shifts every descendant uniformly, so deeper nesting stays
    // proportional.
    paddingLeft: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalXS,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  // État vide d'un espace : bloc centré, volontairement compact (on est DANS
  // l'arbre, pas sur une page pleine — un grand vide pousserait les espaces
  // suivants hors de l'écran).
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXXS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  emptyIcon: { color: tokens.colorNeutralForeground4, fontSize: '24px' },
  emptyTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  emptyHint: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    marginBottom: tokens.spacingVerticalXS,
  },
  createRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
  },
  loadingRow: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  archivedBadge: { marginLeft: tokens.spacingHorizontalXXS, flexShrink: 0 },
  // Visibilité restreinte : un simple CADENAS, plus un badge « Restreint ».
  //
  // Le badge (pastille contourée + mot) pesait autant que le nom de l'espace et
  // repoussait la troncature du titre, alors qu'il ne dit rien qu'un cadenas ne
  // dise. Il reste en revanche un badge dans l'onglet d'inventaire, où la
  // visibilité est une COLONNE que l'on compare d'une ligne à l'autre — le mot y
  // est l'information, pas une décoration.
  //
  // Couleur NEUTRE, comme dans les résultats de recherche : seules les icônes
  // d'élément (dossier, mail) et l'étoile de favori sont colorées ; un indicateur
  // d'état ne doit pas concurrencer le titre.
  restrictedIcon: { flexShrink: 0, color: tokens.colorNeutralForeground3 },
  // Actions révélées au survol. Elles étaient en `display: none`, ce qui les
  // sortait AUSSI de l'ordre de tabulation : le menu ⋯ (favoris, PRISM,
  // SharePoint, visibilité…) devenait alors strictement inatteignable au clavier.
  // On les rend donc invisibles sans les retirer du flux d'accessibilité
  // (`opacity: 0` reste focusable, contrairement à `display: none` ou
  // `visibility: hidden`) et on les réaffiche dès qu'elles prennent le focus
  // clavier — `:focus-visible` pour le déclencheur lui-même, `:focus-within`
  // quand le focus tombe sur un bouton imbriqué.
  // `pointerEvents: 'none'` à l'état masqué est indispensable : un bouton
  // transparent mais cliquable intercepterait les clics destinés à la ligne. Le
  // survol de la ligne repasse la classe à `hoverShown` avant que le pointeur
  // n'atteigne le bouton, donc la souris n'y perd rien.
  hoverOnly: {
    opacity: 0,
    pointerEvents: 'none',
    ':focus-visible': { opacity: 1, pointerEvents: 'auto' },
    ':focus-within': { opacity: 1, pointerEvents: 'auto' },
  },
  hoverShown: {},
  // Conteneur du menu ⋯ : `flex` + `flexShrink: 0` pour qu'il occupe exactement
  // la place que prenait le bouton avant d'être encapsulé.
  menuWrap: { display: 'flex', flexShrink: 0 },
  favStar: { flexShrink: 0, color: '#eaa300', marginLeft: tokens.spacingHorizontalXXS },
});

interface Props {
  container: Container;
}

export function ContainerSection({ container }: Props) {
  const styles = useStyles();
  // Start expanded only if this is already the active container at mount (e.g.
  // restoring the active tab on reload). Later auto-expansion is driven by tab
  // changes in the effect below.
  const [open, setOpen] = useState(
    () => useTreeStore.getState().activeContainerId === container.id,
  );
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuTarget, setMenuTarget] = useState<CursorTarget | null>(null);
  const [visibilityOpen, setVisibilityOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const setActiveContainer = useTreeStore((s) => s.setActiveContainer);
  const activeContainerId = useTreeStore((s) => s.activeContainerId);
  const activeTabId = useTreeStore((s) => s.activeTabId);
  const touchRecent = useRecentProjectsStore((s) => s.touch);
  const favIds = useFavoriteIds();
  const toggleFavorite = useToggleFavorite();
  const isFav = favIds.has(container.id);

  // Auto-unfold this section only on a genuine tab change (user opened it in a
  // new tab or switched tabs) that lands on this container — NOT every time it
  // becomes the active container. Clicking a chevron also marks the container
  // active (via toggle → setActiveContainer); keying auto-open on
  // activeContainerId fought that, re-opening a section the user was collapsing,
  // which forced a second click. activeTabId only changes on real tab nav, so
  // guarding on it leaves manual toggles alone.
  const prevTabIdRef = useRef(activeTabId);
  useEffect(() => {
    const tabChanged = activeTabId !== prevTabIdRef.current;
    prevTabIdRef.current = activeTabId;
    if (tabChanged && activeContainerId === container.id) setOpen(true);
  }, [activeTabId, activeContainerId, container.id]);
  const selection = useSelectionStore((s) => s.items);
  const fileMail = useFileMail();
  const { showLoading, showResults, showError } = useFileMailToasts();
  const expand = useTreeStore((s) => s.expand);
  const openFolderDetail = useTreeStore((s) => s.openFolderDetail);
  const openDetailTab = useTreeStore((s) => s.openDetailTab);
  const { data: folders, isPending } = useFolders(open ? container.id : null);
  const tree = folders ? buildFolderTree(folders) : [];
  const createFolder = useCreateFolder();
  // Mails rangés à la racine : même clé de requête que `ContainerRootMails`,
  // donc React Query mutualise l'appel — on lit juste ici de quoi choisir le bon
  // message d'état vide (« aucun dossier » n'est pas « espace vide »).
  const { data: rootMails } = useRootMails(open ? container.id : null);
  const hasRootMails = (rootMails?.length ?? 0) > 0;
  // Phrase entière et non un mot interpolé : « Cet » + « projet » donnait
  // « Cet projet est vide ».
  const emptyTitle = container.kind === 'project' ? 'Ce projet est vide' : 'Cet espace est vide';

  // Mail-drop onto the project ROOT (folder_id null): the section is the drop
  // target for anything that isn't a specific folder — the header, root mails,
  // and blank space inside it. A FolderRow under the cursor stops propagation,
  // so folder drops never reach here. (New Outlook / web use the Office
  // hit-test on data-drop-container-id instead of these HTML5 handlers.)
  const { fileFiles } = useDropFiler();
  const setDrag = useMailDropStore((s) => s.setDrag);
  const endDragMail = useMailDropStore((s) => s.endDrag);
  const rootDropOver = useMailDropStore(
    (s) => s.target?.kind === 'root' && s.target.containerId === container.id,
  );
  const isFileDrag = (e: DragEvent) => e.dataTransfer?.types?.includes('Files');
  const onSectionDragOver = (e: DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    setDrag({ kind: 'root', containerId: container.id });
  };
  const onSectionDragLeave = (e: DragEvent) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setDrag(null);
  };
  const onSectionDrop = (e: DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    endDragMail();
    const files = extractDroppedFiles(e.nativeEvent);
    addBreadcrumb({
      category: 'office.event',
      message: 'drop-on-project-root',
      data: { containerId: container.id, fileCount: files.length },
    });
    void fileFiles(files, { kind: 'root', containerId: container.id });
  };

  const toggle = () => {
    setOpen((o) => !o);
    setActiveContainer(container.id);
    if (container.kind === 'project') touchRecent(container.id);
  };

  const openCreate = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setOpen(true);
    setCreating(true);
    setMenuOpen(false);
  };

  const submitCreate = () => {
    const name = newName.trim();
    if (!name) {
      setCreating(false);
      return;
    }
    createFolder.mutate(
      { containerId: container.id, parentId: null, name },
      {
        onSettled: () => {
          setCreating(false);
          setNewName('');
        },
      },
    );
  };

  const toggleFav = () => {
    setMenuOpen(false);
    toggleFavorite.mutate({ containerId: container.id, favorite: !isFav });
  };

  const openInNewTab = () => {
    setMenuOpen(false);
    if (container.kind === 'project') touchRecent(container.id);
    // Open the project's root detail view (its folders + root mails) in a fresh
    // tab, leaving the current tab where it is.
    openDetailTab(container.id, null);
  };

  // « Gérer la visibilité » : volontairement SANS condition d'affichage. Décision
  // produit — les droits d'un espace sont consultables et modifiables par tout le
  // monde, pas seulement par les admins de l'add-in.
  const openVisibility = () => {
    setMenuOpen(false);
    setVisibilityOpen(true);
  };

  const rangerIciRoot = () => {
    setMenuOpen(false);
    // Use the container's root folder if a single one exists, otherwise the
    // user has to pick a folder explicitly via the QuickFile view.
    const roots = (folders ?? []).filter((f) => !f.parentId);
    const singleRoot = roots.length === 1 ? roots[0] : undefined;
    if (singleRoot) {
      const target = singleRoot.id;
      const toastId = showLoading(selection.length);
      fileMail.mutate(
        { targetFolderId: target, targetContainerId: container.id, items: selection },
        {
          onSuccess: (results) => {
            showResults(results, toastId);
            if (results.some((r) => r.status === 'created')) {
              setOpen(true);
              expand(target);
            }
          },
          onError: (err) => showError(err instanceof Error ? err.message : String(err), toastId),
        },
      );
    } else {
      // Fall back: open the container and let the user pick.
      setOpen(true);
    }
  };

  // 16 et non 20, comme les icônes de dossier et de mail (cf. ItemIcon) : à 20 px
  // la mallette dominait le numéro de projet qu'elle précède.
  const Icon = container.kind === 'project' ? Briefcase16Regular : People16Regular;
  const isLogicallyArchived =
    container.archived ||
    (container.kind === 'project' &&
      (container.status === 'termine' || container.status === 'annule'));
  const labelClasses = [
    styles.label,
    container.archived ? styles.archived : '',
    container.kind === 'project' && container.status && container.status !== 'actif'
      ? styles.inactive
      : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div
        data-drop-container-id={container.id}
        onDragOver={onSectionDragOver}
        onDragEnter={onSectionDragOver}
        onDragLeave={onSectionDragLeave}
        onDrop={onSectionDrop}
      >
        <div
          className={`${styles.header} ${rootDropOver ? styles.headerRootDropOver : ''}`}
          onClick={(e) => {
            // Skip the second click of a double-click so the section doesn't
            // expand-then-collapse before the detail page opens.
            if (e.detail > 1) return;
            toggle();
          }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            openFolderDetail(container.id, null);
          }}
          title={container.name}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setMenuTarget(pointTarget(e.clientX, e.clientY));
            setMenuOpen(true);
          }}
        >
          <Button
            appearance="subtle"
            size="small"
            icon={open ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
            onClick={(e) => {
              e.stopPropagation();
              toggle();
            }}
          />
          <Icon className={styles.headerIcon} />
          <span className={styles.labelWrap}>
            <span className={labelClasses}>{container.name}</span>
            {/* Visibilité restreinte : le champ existe depuis toujours dans le
                type Container mais n'était jamais rendu, donc rien ne signalait
                qu'un espace n'était plus visible de tous. Un simple cadenas COLLÉ
                au titre — c'est le nom de l'espace qu'il qualifie —, avec une
                infobulle qui explique la conséquence et pas seulement l'état. */}
            {container.isRestricted && (
              <LockClosed12Regular
                className={styles.restrictedIcon}
                title="Accès limité : seules les personnes et les groupes autorisés voient cet espace."
              />
            )}
          </span>
          {isFav && <Star16Filled className={styles.favStar} title="Favori" />}
          {isLogicallyArchived && (
            <Badge
              className={styles.archivedBadge}
              appearance="outline"
              color="subtle"
              size="small"
              title={
                container.status === 'annule'
                  ? 'Projet annulé'
                  : container.status === 'termine'
                    ? 'Projet terminé'
                    : 'Projet archivé'
              }
            >
              Archivé
            </Badge>
          )}
          {fileMail.isPending ? (
            <Tooltip content="Rangement en cours…" relationship="label">
              <Spinner size="tiny" />
            </Tooltip>
          ) : (
            selection.length > 0 && (
              <div className={hover ? styles.hoverShown : styles.hoverOnly}>
                <Tooltip content={`Ranger ${selection.length} mail(s) ici`} relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<MailArrowUp20Regular />}
                    onClick={(e) => {
                      e.stopPropagation();
                      rangerIciRoot();
                    }}
                  />
                </Tooltip>
              </div>
            )
          )}
          {/* CE WRAPPER N'EST PAS DÉCORATIF. Fluent monte le MenuPopover dans un
              PORTAIL, mais React fait remonter les événements du portail dans
              l'arbre REACT, pas dans l'arbre DOM : un clic sur « Gérer la
              visibilité » ou sur une entrée du sous-menu bulle donc jusqu'au
              `onClick` de l'en-tête et déclenchait `toggle()` — la section
              s'ouvrait ou se refermait en même temps que l'action, exactement
              comme si on avait cliqué sur le chevron. `onDoubleClick` est stoppé
              pour la même raison (l'en-tête ouvre la page détail sur double-clic).
              Même remède que `MailRow` et que la colonne d'actions de
              `FolderDetail`, où le piège avait déjà été rencontré. */}
          <div
            className={styles.menuWrap}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
          <Menu
            open={menuOpen}
            onOpenChange={(_e, data) => {
              setMenuOpen(data.open);
              // Cf. FolderRow : l'ancre curseur ne doit pas survivre au menu.
              if (!data.open) setMenuTarget(null);
            }}
            positioning={menuPositioning(menuTarget)}
          >
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="subtle"
                size="small"
                icon={<MoreHorizontalRegular />}
                className={hover || menuOpen ? styles.hoverShown : styles.hoverOnly}
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuTarget(null);
                  setMenuOpen(true);
                }}
              />
            </MenuTrigger>
            <MenuPopover>
              {/* ORDRE DU MENU — la même grammaire dans tous les menus contextuels
                  de l'add-in (dossier, mail, espace), du plus immédiat au plus
                  lourd de conséquences :
                    1. l'action attendue tout de suite (ranger la sélection) ;
                    2. ouvrir / naviguer ;
                    3. créer ;
                    4. sortir de l'add-in (sous-menu « Espaces collaboratifs ») ;
                    5. réglages de l'espace (favoris, visibilité).
                  Les séparateurs matérialisent ces familles : avant, sept entrées
                  se suivaient à plat et « Ranger 1 mail ici » se retrouvait entre
                  « Retirer des favoris » et « Nouveau dossier ». */}
              <MenuList>
                {selection.length > 0 && (
                  <>
                    <MenuItem icon={<MailArrowUp20Regular />} onClick={rangerIciRoot}>
                      Ranger {selection.length} mail(s) ici
                    </MenuItem>
                    <MenuDivider />
                  </>
                )}

                <MenuItem icon={<TabAddRegular />} onClick={openInNewTab}>
                  Ouvrir dans un nouvel onglet
                </MenuItem>
                <MenuItem icon={<FolderAdd20Regular />} onClick={openCreate}>
                  Nouveau dossier
                </MenuItem>

                <MenuDivider />
                {/* Le sous-menu porte lui-même la fermeture du menu parent
                    (Fluent referme toute la cascade au clic sur une entrée). */}
                <CollaborationMenu container={container} />

                <MenuDivider />
                <MenuItem icon={isFav ? <Star20Filled /> : <Star20Regular />} onClick={toggleFav}>
                  {isFav ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                </MenuItem>
                {/* Dernier item : c'est une action de gouvernance de l'espace, pas
                    une action de navigation ou de rangement. Icône en variante
                    Regular comme les autres icônes d'ACTION du menu. */}
                <MenuItem icon={<PeopleLock20Regular />} onClick={openVisibility}>
                  Gérer la visibilité
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
          </div>
        </div>
        {open && (
          <div className={styles.body}>
            {isPending && (
              <div className={styles.loadingRow}>
                <Skeleton>
                  <SkeletonItem size={16} style={{ width: '70%' }} />
                  <SkeletonItem size={16} style={{ width: '55%', marginTop: 6 }} />
                  <SkeletonItem size={16} style={{ width: '60%', marginTop: 6 }} />
                </Skeleton>
              </div>
            )}
            {!isPending && tree.length === 0 && !creating && (
              // État vide REFORMULÉ ET RESTRUCTURÉ. Trois défauts corrigés :
              //  • « ce projet » s'affichait aussi sur un espace de service
              //    (« Pôle Développement »), où le mot est faux ;
              //  • le texte et le bouton se suivaient sur la même ligne, si bien
              //    que le bouton passait pour la fin de la phrase ;
              //  • rien ne distinguait « aucun dossier, mais des mails à la
              //    racine » de « espace complètement vide » — deux situations
              //    différentes pour qui découvre l'espace.
              <div className={styles.emptyState}>
                <FolderAdd20Regular className={styles.emptyIcon} />
                <span className={styles.emptyTitle}>
                  {hasRootMails ? 'Aucun dossier pour organiser ces mails' : emptyTitle}
                </span>
                <span className={styles.emptyHint}>
                  {hasRootMails
                    ? 'Les mails ci-dessous sont rangés à la racine. Créez des dossiers pour les classer.'
                    : 'Créez un premier dossier, ou glissez-y directement des mails.'}
                </span>
                <Button appearance="primary" size="small" onClick={openCreate}>
                  Créer un dossier
                </Button>
              </div>
            )}
            {tree.map((node) => (
              <FolderRow key={node.folder.id} node={node} depth={0} />
            ))}
            {creating && (
              <div className={styles.createRow}>
                <Input
                  size="small"
                  autoFocus
                  placeholder="Nom du dossier"
                  value={newName}
                  disabled={createFolder.isPending}
                  onChange={(_e, data) => setNewName(data.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitCreate();
                    if (e.key === 'Escape') {
                      setCreating(false);
                      setNewName('');
                    }
                  }}
                  onBlur={submitCreate}
                />
              </div>
            )}
            {/* Mails rangés à la RACINE de l'espace (`folder_id` null), APRÈS les
                dossiers — même ordre que la page détail, où les dossiers viennent
                d'abord et les mails ensuite.
                Ils s'affichaient au-dessus, ce qui repoussait l'arborescence vers
                le bas : à l'ouverture d'un espace, on cherche d'abord un dossier,
                et une pile de mails non classés séparait l'en-tête de ses propres
                dossiers. L'entrée de saisie d'un nouveau dossier reste, elle,
                collée à la liste des dossiers à laquelle elle appartient. */}
            <ContainerRootMails containerId={container.id} depth={0} />
          </div>
        )}
      </div>
      {/* Monté HORS du div de drop, comme les dialogues de FolderRow : un portail
          React fait remonter ses événements dans l'arbre REACT, donc un clic
          dans le dialogue traverserait sinon les handlers de la section. */}
      {visibilityOpen && (
        <ContainerVisibilityDialog
          container={container}
          open
          onOpenChange={(o) => {
            if (!o) setVisibilityOpen(false);
          }}
        />
      )}
    </>
  );
}
